/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo } from 'react'
import { DateTime } from 'luxon'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Box, Table, Theme } from '@nexigendigital/nxui'
import { Weight } from 'models/enums'
import { useAppSelector } from 'store/hooks'
import { getInitials, parseISO } from 'helpers/utils'

/**********************************************************************************************************
 *   STYLE
 **********************************************************************************************************/
import styled from 'styled-components'

const User = {
    Base: styled(Box)`
        height: min-content;
        background-color: ${props => (props.theme['white--100'])};
        margin-bottom: 0;
        width: 100%;
    `,
    Profile: {
        Container: styled.div`
            display: flex;
            flex-direction: row;
            padding: 20px;

            > div:last-of-type {
                display: flex;
                flex-direction: column;
                justify-content: center;
                margin-left: 20px;
            }
        `,
        Title: {
            Name: styled.h1`
                font-size: 1.5rem;
                line-height: 1;
                ${Theme.Functions.setFont('Inter', Weight.Medium)};
            `,
            Role: styled.p`
                font-size: 1rem;
                color: ${props => (props.theme['primary--100'])};
                ${Theme.Functions.setFont('Inter', Weight.Medium)};
            `
        },
        Avatar: styled.div`
            display: flex;
            align-items: center;
            justify-content: center;
            height: 60px;
            width: 60px;
            min-width: 60px;
            border-radius: 9999px;
            color: ${props => (props.theme['white--100'])};
            background-color: ${Theme.Functions.randomColor()};
            font-size: 1.5rem;
            ${Theme.Functions.setFont('Inter', Weight.Medium)};

            ${props => (Theme.Functions.mediaBreakUp(props.theme['breakpoints-sm']))} {
                height: 80px;
                width: 80px;
                min-width: 80px;
                font-size: 2rem;
            }
        `
    },
    Detail: styled.div`
        text-align: right;
        color: ${props => (props.theme['text--300'])};
        ${Theme.Functions.setFont('Inter', Weight.Medium)};
    `,
    Table: styled(Table)`
        border-top: 1px solid ${props => (props.theme['gray--100'])};

        > tbody > .nxTable__row {
            border-left: 0;
            border-right: 0;
        }

        > tbody > .nxTable__row > .nxTable__rowCell:first-of-type {
            width: 150px;
        }

        > tbody > .nxTable__row > .nxTable__rowCell:last-of-type {
            text-align: right;
        }
    `,
    Footer: styled.div`
        display: flex;
        justify-content: space-between;

        button {
            width: 100%;
        }
    `
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Profile() {
    const { appSession: { user: userInformation, account } } = useAppSelector((state) => state.app)
    const { firstname, lastname } = userInformation!
    const { role } = account!
    const userSummary = useMemo(() => {
        const {
            customer_since: customerSince,
            last_login: lastLogin
        } = userInformation!
        const {
            account_number
        } = account!

        return [
            {
                title: 'Account No.',
                detail: <User.Detail>{account_number}</User.Detail>
            },
            {
                title: 'Customer Since',
                detail: <User.Detail>{customerSince ? parseISO(customerSince).setLocale('en-AU').toLocaleString() : ''}</User.Detail>
            },
            {
                title: 'Last Login',
                detail: <User.Detail>{lastLogin ? DateTime.fromSeconds(lastLogin?.time).setLocale('en-AU').toLocaleString() : ''}</User.Detail>
            },
            {
                title: 'IP Address',
                detail: <User.Detail>{lastLogin?.ip}</User.Detail>
            }
        ]
    }, [userInformation])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <>
            <User.Base>
                <User.Profile.Container>
                    <User.Profile.Avatar>{getInitials(`${firstname} ${lastname}`)}</User.Profile.Avatar>
                    <div>
                        <User.Profile.Title.Name>{`${firstname} ${lastname}`}</User.Profile.Title.Name>
                        <User.Profile.Title.Role>{`${role?.name}`}</User.Profile.Title.Role>
                    </div>
                </User.Profile.Container>
                <User.Table
                    conditions={{
                        header: false,
                        embedded: true
                    }}
                    render={{
                        columns: () => [
                            {
                                Header: '',
                                accessor: 'title'
                            },
                            {
                                Header: '',
                                accessor: 'detail'
                            }
                        ],
                        data: () => userSummary
                    }}
                />
            </User.Base>
        </>
    )
}
